import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import {
  getMyEmailPreferences,
  updateMyEmailPreferences,
  type EmailPreferences,
} from "@/lib/email-preferences.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/settings/email-preferences")({
  head: () => ({
    meta: [
      { title: "Email Preferences — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Choose which Recovery Trauma™ emails you receive: product updates, membership reminders, new features, events, newsletter, therapist updates, and promotions.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EmailPreferencesPage,
});

type Row = { key: keyof EmailPreferences; label: string; desc: string };

const CATEGORIES: Row[] = [
  { key: "product_updates", label: "Product updates", desc: "Improvements, changes and announcements about the platform." },
  { key: "membership_reminders", label: "Membership reminders", desc: "Prompts to finish signup, verify your email, or complete Member checkout." },
  { key: "new_features", label: "New features", desc: "First look at new tools, resources and experiences." },
  { key: "events_webinars", label: "Events & webinars", desc: "Live sessions, workshops and community events." },
  { key: "newsletter", label: "Newsletter", desc: "Our regular Recovery Trauma™ newsletter with articles and reflections." },
  { key: "therapist_updates", label: "Therapist updates", desc: "News from the Recovery Trauma™ therapist directory." },
  { key: "marketing_promotional", label: "Marketing & promotional", desc: "Special offers, campaigns and referral opportunities." },
];

function EmailPreferencesPage() {
  const fetcher = useServerFn(getMyEmailPreferences);
  const saver = useServerFn(updateMyEmailPreferences);
  const [prefs, setPrefs] = useState<EmailPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) {
        setSignedIn(false);
        return;
      }
      setSignedIn(true);
      fetcher().then(setPrefs).catch((e) => toast.error(e?.message || "Failed to load preferences"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(key: keyof EmailPreferences) {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await saver({ data: next });
      toast.success("Preferences saved");
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  }

  async function setAll(value: boolean) {
    if (!prefs) return;
    const next: EmailPreferences = {
      product_updates: value,
      membership_reminders: value,
      new_features: value,
      events_webinars: value,
      newsletter: value,
      therapist_updates: value,
      marketing_promotional: value,
    };
    setPrefs(next);
    setSaving(true);
    try {
      await saver({ data: next });
      toast.success(value ? "Subscribed to all" : "Unsubscribed from all marketing emails");
    } catch (e: any) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (signedIn === false) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <Mail className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm">Please sign in to manage your email preferences.</p>
        <Link to="/login" className="mt-4 inline-block text-sm underline">Sign in</Link>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link to="/profile" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to profile
      </Link>

      <h1 className="font-serif text-3xl text-foreground">Email preferences</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Choose which Recovery Trauma™ emails you'd like to receive. Changes save automatically.
      </p>

      <div className="mt-6 flex gap-2">
        <button
          onClick={() => setAll(true)}
          disabled={saving}
          className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-40"
        >
          Subscribe to all
        </button>
        <button
          onClick={() => setAll(false)}
          disabled={saving}
          className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-40"
        >
          Unsubscribe from all
        </button>
      </div>

      <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
        {CATEGORIES.map((c) => (
          <label
            key={c.key}
            className="flex cursor-pointer items-start gap-4 p-4 hover:bg-muted/30"
          >
            <input
              type="checkbox"
              checked={prefs[c.key]}
              onChange={() => toggle(c.key)}
              disabled={saving}
              className="mt-1 h-5 w-5 rounded border-border text-[var(--gold-deep)] focus:ring-gold"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{c.label}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{c.desc}</p>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${prefs[c.key] ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200" : "bg-muted text-foreground dark:bg-white/10 dark:text-foreground/70"}`}>
              {prefs[c.key] ? "ON" : "OFF"}
            </span>
          </label>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-muted/30 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--gold-deep)]" />
          <div>
            <p className="text-sm font-semibold text-foreground">Essential emails always send</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Regardless of the settings above, we will always send you emails required to operate your account:
              email verification, password reset, login and security alerts, payment receipts, subscription renewals,
              failed payment notices, cancellation confirmations, and important service announcements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
